// src/pages/SearchResultsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { searchGlobalPosts, searchSubreddits } from '../api/redditApi';
import axios from 'axios';
import { PostData, SubredditData } from '../types/reddit'; // Import types
import PostCard from '../components/PostCard'; // Import PostCard
import { BLOCK_NSFW } from '../utils/settings'; // Import NSFW settings


const SearchResultsPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q');

    const [postResults, setPostResults] = useState<PostData[]>([]);
    const [subredditResults, setSubredditResults] = useState<SubredditData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [afterPosts, setAfterPosts] = useState<string | null>(null);
    const [afterSubreddits, setAfterSubreddits] = useState<string | null>(null);
    const [hasMorePosts, setHasMorePosts] = useState<boolean>(true);
    const [hasMoreSubreddits, setHasMoreSubreddits] = useState<boolean>(true);
    const [searchType, setSearchType] = useState<'posts' | 'subreddits'>('posts'); // Default to posts

    const { ref: loadMoreRef, inView: loadMoreInView } = useInView({ threshold: 0.1, rootMargin: '200px' });

    // Memoized mapping functions
    const mapApiPostToData = useCallback((child: { data: any }): PostData => ({
        id: child.data.id, name: child.data.name, title: child.data.title, author: child.data.author,
        subreddit: child.data.subreddit, subreddit_name_prefixed: child.data.subreddit_name_prefixed,
        thumbnail: child.data.thumbnail, url: child.data.url, domain: child.data.domain,
        is_self: child.data.is_self, score: child.data.score, created_utc: child.data.created_utc,
        num_comments: child.data.num_comments, permalink: child.data.permalink, post_hint: child.data.post_hint,
        thumbnail_width: child.data.thumbnail_width, thumbnail_height: child.data.thumbnail_height,
        selftext: child.data.selftext || '',
        is_video: child.data.is_video, over_18: child.data.over_18, // Add over_18
     }), []);
    
    const mapApiSubredditToData = useCallback((child: any): SubredditData => ({
        id: child.data.id, name: child.data.name, display_name: child.data.display_name,
        display_name_prefixed: child.data.display_name_prefixed,
        // Use community_icon as fallback for icon_img
        icon_img: child.data.icon_img || child.data.community_icon || '',
        url: child.data.url, // Relative URL like /r/reactjs/
        subscribers: child.data.subscribers,
        public_description: child.data.public_description,
        over18: child.data.over18, // Add over18
    }), []);

    // Fetch results function
    const fetchResults = useCallback(async (currentQuery: string, type: 'posts' | 'subreddits', loadAfter: string | null) => {
        if (!currentQuery) return; // Don't fetch if query is empty

        if (!loadAfter) {
            // Initial load
            setIsLoading(true);
            if (type === 'posts') { setPostResults([]); setHasMorePosts(true); }
            else { setSubredditResults([]); setHasMoreSubreddits(true); }
        } else {
            // Loading more
            setIsLoadingMore(true);
        }
        setError(null); // Clear previous errors

        try {
            const params: Record<string, any> = { limit: 25 };
            if (loadAfter) params.after = loadAfter;

            let response;
            if (type === 'posts') {
                response = await searchGlobalPosts(currentQuery, params);
                let fetchedPosts = response?.data?.children?.map(mapApiPostToData) || [];
                // Filter NSFW posts if blocked
                if (BLOCK_NSFW) {
                    fetchedPosts = fetchedPosts.filter((post: { over_18: any; }) => !post.over_18);
                }
                const newAfter = response?.data?.after;
                setPostResults(prev => loadAfter ? [...prev, ...fetchedPosts] : fetchedPosts);
                setAfterPosts(newAfter);
                setHasMorePosts(!!newAfter && fetchedPosts.length > 0);
            } else { // type === 'subreddits'
                response = await searchSubreddits(currentQuery, params);
                let fetchedSubreddits = response?.data?.children?.map(mapApiSubredditToData) || [];
                 // Filter NSFW subreddits if blocked
                 if (BLOCK_NSFW) {
                    fetchedSubreddits = fetchedSubreddits.filter((sub: { over18: any; }) => !sub.over18);
                 }
                const newAfter = response?.data?.after;
                setSubredditResults(prev => loadAfter ? [...prev, ...fetchedSubreddits] : fetchedSubreddits);
                setAfterSubreddits(newAfter);
                setHasMoreSubreddits(!!newAfter && fetchedSubreddits.length > 0);
            }
        } catch (err: any) {
            console.error(`Failed search results for ${type}:`, err);
            if (err.name !== 'CanceledError' && !axios.isCancel(err)) {
                setError(`Failed to load search results. ${err.message}`);
                // Stop pagination on error
                if (type === 'posts') setHasMorePosts(false);
                else setHasMoreSubreddits(false);
            }
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [mapApiPostToData, mapApiSubredditToData]); // Dependencies for the fetch callback

    // Effect for initial fetch when query or searchType changes
    useEffect(() => {
        if (query) {
            // Reset the *other* type's results when switching tabs
            if (searchType === 'posts') {
                setSubredditResults([]); setAfterSubreddits(null); setHasMoreSubreddits(true);
            } else { // searchType === 'subreddits'
                setPostResults([]); setAfterPosts(null); setHasMorePosts(true);
            }
            // Fetch results for the current type
            fetchResults(query, searchType, null);
        } else {
            // Clear everything if query is removed/empty
            setPostResults([]);
            setSubredditResults([]);
            setError(null);
            setIsLoading(false);
            setIsLoadingMore(false);
            setAfterPosts(null);
            setAfterSubreddits(null);
            setHasMorePosts(true);
            setHasMoreSubreddits(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, searchType]); // Rerun when query or searchType changes

    // Effect for infinite scroll
    useEffect(() => {
        if (loadMoreInView && !isLoading && !isLoadingMore && query) {
            if (searchType === 'posts' && hasMorePosts && afterPosts) {
                fetchResults(query, 'posts', afterPosts);
            } else if (searchType === 'subreddits' && hasMoreSubreddits && afterSubreddits) {
                fetchResults(query, 'subreddits', afterSubreddits);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadMoreInView, isLoading, isLoadingMore, hasMorePosts, hasMoreSubreddits, afterPosts, afterSubreddits, query, searchType]);

    const currentResults = searchType === 'posts' ? postResults : subredditResults;
    const hasMore = searchType === 'posts' ? hasMorePosts : hasMoreSubreddits;

    return (
        <div className="content-wrapper space-y-10">
            {/* Show prompt if no query */}
            {!query && !isLoading && (
                 <div className="text-center text-gray-500 dark:text-gray-400 pt-10">
                     Use the search bar in the header to find posts or subreddits across Reddit.
                 </div>
            )}

            {/* Show results area only if there is a query */}
            {query && (
                <>
                    {/* Page Header */}
                    <header className="space-y-4">
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white break-words">
                            Search results for "{query}"
                        </h1>
                        {/* Search Type Tabs */}
                        <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
                             <button
                                onClick={() => setSearchType('posts')}
                                className={`pb-2 text-sm font-medium focus:outline-none ${
                                    searchType === 'posts'
                                    ? 'border-b-2 border-indigo-600 dark:border-indigo-400 text-indigo-700 dark:text-indigo-400'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 border-b-2 border-transparent'
                                }`}
                                aria-current={searchType === 'posts' ? 'page' : undefined}
                             >
                                 Posts
                             </button>
                             <button
                                onClick={() => setSearchType('subreddits')}
                                className={`pb-2 text-sm font-medium focus:outline-none ${
                                    searchType === 'subreddits'
                                    ? 'border-b-2 border-indigo-600 dark:border-indigo-400 text-indigo-700 dark:text-indigo-400'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 border-b-2 border-transparent'
                                }`}
                                aria-current={searchType === 'subreddits' ? 'page' : undefined}
                             >
                                 Subreddits
                             </button>
                        </div>
                    </header>

                    {/* Loading Skeleton */}
                    {isLoading && (
                        <div className="space-y-6 animate-pulse">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="p-4 md:p-5 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50 flex gap-4">
                                    <div className="flex-1 space-y-3">
                                        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                                    </div>
                                    <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-200 dark:bg-gray-700 rounded-md flex-shrink-0"></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Error Message */}
                    {error && !isLoading && !isLoadingMore && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm" role="alert">
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {/* Results List */}
                    {!isLoading && currentResults.length > 0 && (
                        <section className="space-y-6">
                            {/* === POST RESULTS (Using PostCard) === */}
                            {searchType === 'posts' && postResults.map((post) => (
                                <PostCard
                                    key={post.id}
                                    post={post}
                                    isHomePage={true} // isHomePage=true shows subreddit link
                                    // Assume NSFW allowed for global search (PostCard handles thumbnail)
                                    isNsfwAllowedForSubreddit={true}
                                />
                            ))}

                            {/* === SUBREDDIT RESULTS (Minimal List Style) === */}
                            {searchType === 'subreddits' && (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700 border-t border-b border-gray-200 dark:border-gray-700">
                                    {subredditResults.map((sub) => (
                                        <article key={sub.id} className="py-3 flex items-center space-x-4">
                                            {/* Subreddit Icon */}
                                            <div className="flex-shrink-0">
                                                {sub.icon_img ? (
                                                    <img src={sub.icon_img} alt="" loading="lazy" className="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700 object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                                ) : (
                                                    // Fallback circle with initial
                                                    <span className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs font-medium">
                                                        {sub.display_name_prefixed.charAt(2).toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Subreddit Info */}
                                            <div className="flex-1 min-w-0">
                                                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                                                    <Link to={sub.url} className="hover:underline focus:underline" title={`Go to ${sub.display_name_prefixed}`}>
                                                        {sub.display_name_prefixed}
                                                    </Link>
                                                    {sub.over18 && <span className="ml-1.5 text-[9px] font-bold text-red-600 dark:text-red-400 border border-red-500 dark:border-red-400 px-0.5 rounded-sm leading-none align-middle">NSFW</span>}
                                                </h2>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 space-x-2">
                                                    {sub.subscribers != null && <span>{sub.subscribers.toLocaleString()} members</span>}
                                                </div>
                                                {sub.public_description && (
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1" title={sub.public_description}>
                                                        {sub.public_description}
                                                    </p>
                                                )}
                                            </div>
                                            {/* Visit Button */}
                                            <Link
                                                to={sub.url}
                                                className="ml-auto flex-shrink-0 px-2.5 py-0.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500"
                                            >
                                                Visit
                                            </Link>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}

                    {/* Load More & End of Results */}
                    {!isLoading && hasMore && currentResults.length > 0 && (
                         <div ref={loadMoreRef} className="h-20 flex justify-center items-center pt-6">
                             {isLoadingMore && (
                                 <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 text-sm">
                                     <svg className="animate-spin h-4 w-4 text-indigo-600 dark:text-indigo-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                     <span>Loading...</span>
                                 </div>
                             )}
                         </div>
                    )}
                    {!isLoading && !isLoadingMore && !hasMore && currentResults.length > 0 && (
                         <div className="h-10 flex justify-center items-center pt-6">
                             <p className="text-sm text-gray-500 dark:text-gray-400">End of results.</p>
                         </div>
                    )}
                    {/* No Results Found */}
                    {!isLoading && !isLoadingMore && !error && currentResults.length === 0 && (
                         <div className="text-center text-gray-500 dark:text-gray-400 pt-10">
                             <p>No {searchType} found matching "{query}".</p>
                             {BLOCK_NSFW && <p className="text-xs mt-1">(NSFW results are hidden)</p>}
                         </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SearchResultsPage;